import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ThemeService } from '../services/themeService';
import type { Theme } from '../services/themeService';

interface ThemeState {
  currentTheme: Theme;
  availableThemes: Theme[];
  isLoading: boolean;
  error: string | null;
}

interface ThemeActions {
  setCurrentTheme: (theme: Theme) => void;
  loadThemes: () => Promise<void>;
  createTheme: (theme: Theme) => Promise<void>;
  updateTheme: (theme: Theme) => Promise<void>;
  deleteTheme: (themeId: string) => Promise<void>;
  previewTheme: (theme: Theme) => void;
  applyTheme: (theme: Theme) => void;
  resetToDefault: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useThemeStore = create<ThemeState & ThemeActions>()(
  persist(
    (set, get) => ({
      // Initial state
      currentTheme: ThemeService.getCurrentTheme(),
      availableThemes: ThemeService.getAllThemes(),
      isLoading: false,
      error: null,

      // Actions
      setCurrentTheme: (theme) => {
        set({ currentTheme: theme });
        ThemeService.applyTheme(theme);
      },

      loadThemes: async () => {
        set({ isLoading: true, error: null });
        try {
          const themes = ThemeService.getAllThemes();
          set({
            availableThemes: themes,
            currentTheme: ThemeService.getCurrentTheme(),
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load themes',
            isLoading: false
          });
        }
      },

      createTheme: async (theme) => {
        set({ isLoading: true, error: null });
        try {
          ThemeService.saveTheme(theme);
          const themes = ThemeService.getAllThemes();
          set({
            availableThemes: themes,
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create theme',
            isLoading: false
          });
        }
      },

      updateTheme: async (theme) => {
        set({ isLoading: true, error: null });
        try {
          ThemeService.updateTheme(theme);
          const themes = ThemeService.getAllThemes();
          set({
            availableThemes: themes,
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update theme',
            isLoading: false
          });
        }
      },

      deleteTheme: async (themeId) => {
        set({ isLoading: true, error: null });
        try {
          ThemeService.deleteTheme(themeId);
          const themes = ThemeService.getAllThemes();
          set({
            availableThemes: themes,
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete theme',
            isLoading: false
          });
        }
      },

      previewTheme: (theme) => {
        ThemeService.previewTheme(theme);
      },

      applyTheme: (theme) => {
        ThemeService.applyTheme(theme);
        set({ currentTheme: theme });
      },

      resetToDefault: () => {
        const defaultTheme = ThemeService.getAllThemes().find(t => t.id === 'default-white');
        if (defaultTheme) {
          ThemeService.applyTheme(defaultTheme);
          set({ currentTheme: defaultTheme });
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      clearError: () => set({ error: null })
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        currentTheme: state.currentTheme
      })
    }
  )
);

